import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, Upload, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LookupSelect from "@/components/forms/LookupSelect";
import { useLookups } from "@/contexts/LookupsContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";

export default function ProducerProfileForm({ fetchUrl = "/profiles/producer/me", saveUrl = "/profiles/producer/me", showLogoUpload = true, onSaved }) {
  const { t } = useTranslation();
  const lookups = useLookups();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sirenLookupLoading, setSirenLookupLoading] = useState(false);

  useEffect(() => {
    setProfile(null);
    api.get(fetchUrl).then(({ data }) => setProfile(data));
  }, [fetchUrl]);

  const update = (field, value) => setProfile((p) => ({ ...p, [field]: value }));

  const handleSirenLookup = async () => {
    if (!profile.siren) return;
    setSirenLookupLoading(true);
    try {
      const { data } = await api.get("/profiles/producer/siren-lookup", { params: { siren: profile.siren } });
      setProfile((p) => ({
        ...p,
        company_name: p.company_name || data.company_name,
        billing_address: data.billing_address,
        billing_postal_code: data.billing_postal_code,
        billing_city: data.billing_city,
        billing_country: data.billing_country,
      }));
      toast.success(t("common.success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSirenLookupLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...profile };
      delete payload.id;
      delete payload.user_id;
      delete payload.email;
      delete payload.logo_path;
      delete payload.is_complete;
      delete payload.updated_at;
      delete payload.stripe_customer_id;
      delete payload.free_boost_used;
      const { data } = await api.put(saveUrl, payload);
      toast.success(t("producerProfile.profileSaved"));
      setProfile((p) => ({ ...p, is_complete: data.is_complete }));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/profiles/producer/me/logo", formData);
      setProfile((p) => ({ ...p, logo_path: data.logo_path }));
      toast.success(t("common.success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  if (!profile || !lookups) {
    return <div className="h-40 flex items-center justify-center text-sm text-[#2B2B2B]/50">{t("common.loading")}</div>;
  }

  return (
    <div className="space-y-8" data-testid="producer-profile-form">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold">{t("producerProfile.title")}</h2>
        <Badge className={profile.is_complete ? "bg-[#34C759]/10 text-[#34C759] border-0" : "bg-[#dde165]/20 text-[#2B2B2B] border-0"}>
          {profile.is_complete ? t("candidateProfile.profileComplete") : t("candidateProfile.profileIncomplete")}
        </Badge>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-xl bg-[#FAFAFA] border border-black/10 flex items-center justify-center overflow-hidden">
          {profile.logo_path ? (
            <img src={`${API}/profiles/files/${profile.logo_path}`} alt="" className="h-full w-full object-cover" />
          ) : (
            <Building2 className="h-6 w-6 text-[#2B2B2B]/30" />
          )}
        </div>
        {showLogoUpload && (
          <label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#4ba3b7] hover:underline">
              <Upload className="h-4 w-4" /> {t("producerProfile.logoUpload")}
            </span>
            <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={handleLogoUpload} data-testid="producer-logo-upload-input" />
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>{t("producerProfile.companyName")}</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={profile.company_name || ""} onChange={(e) => update("company_name", e.target.value)} data-testid="producer-company-name-input" />
        </div>
        <div>
          <Label>{t("producerProfile.contactFirstName")}</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={profile.contact_first_name || ""} onChange={(e) => update("contact_first_name", e.target.value)} data-testid="producer-contact-first-name-input" />
        </div>
        <div>
          <Label>{t("producerProfile.contactLastName")}</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={profile.contact_last_name || ""} onChange={(e) => update("contact_last_name", e.target.value)} data-testid="producer-contact-last-name-input" />
        </div>
        <div>
          <Label>{t("producerProfile.phone")}</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={profile.phone || ""} onChange={(e) => update("phone", e.target.value)} data-testid="producer-phone-input" />
        </div>
        <div>
          <Label className="mb-1.5 block">{t("producerProfile.country")}</Label>
          <LookupSelect options={lookups.countries} value={profile.country} onChange={(v) => update("country", v)}
                        labelPrefix="lookups.countries" testId="producer-country-select" />
        </div>
        <div>
          <Label className="mb-1.5 block">{t("producerProfile.region")}</Label>
          <LookupSelect options={lookups.regions_by_country[profile.country] || []} value={profile.region} onChange={(v) => update("region", v)}
                        labelPrefix="lookups.regions" testId="producer-region-select" />
        </div>
        <div>
          <Label>{t("producerProfile.address")}</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={profile.address || ""} onChange={(e) => update("address", e.target.value)} data-testid="producer-address-input" />
        </div>
        <div className="sm:col-span-2">
          <Label>{t("producerProfile.googleBusinessUrl")}</Label>
          <Input className="h-12 rounded-xl mt-1.5" placeholder="https://g.page/..." value={profile.google_business_url || ""} onChange={(e) => update("google_business_url", e.target.value)} data-testid="producer-google-business-url-input" />
        </div>
      </div>

      <div className="pt-4 border-t border-black/5">
        <h3 className="font-heading text-lg font-bold mb-1">{t("producerProfile.billingSectionTitle")}</h3>
        <p className="text-sm text-[#2B2B2B]/50 mb-4">{t("producerProfile.billingSectionDesc")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 flex items-end gap-2">
            <div className="flex-1">
              <Label>{t("producerProfile.siren")}</Label>
              <Input maxLength={9} className="h-12 rounded-xl mt-1.5" placeholder="123456789" value={profile.siren || ""} onChange={(e) => update("siren", e.target.value)} data-testid="producer-siren-input" />
            </div>
            <Button type="button" variant="outline" onClick={handleSirenLookup} disabled={sirenLookupLoading || !profile.siren}
                    className="h-12 rounded-xl gap-2" data-testid="producer-siren-lookup-button">
              {sirenLookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {t("producerProfile.sirenLookup")}
            </Button>
          </div>
          <div className="sm:col-span-2">
            <Label>{t("producerProfile.billingAddress")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.billing_address || ""} onChange={(e) => update("billing_address", e.target.value)} data-testid="producer-billing-address-input" />
          </div>
          <div>
            <Label>{t("producerProfile.billingPostalCode")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.billing_postal_code || ""} onChange={(e) => update("billing_postal_code", e.target.value)} data-testid="producer-billing-postal-code-input" />
          </div>
          <div>
            <Label>{t("producerProfile.billingCity")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={profile.billing_city || ""} onChange={(e) => update("billing_city", e.target.value)} data-testid="producer-billing-city-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("producerProfile.billingCountry")}</Label>
            <LookupSelect options={lookups.countries} value={profile.billing_country} onChange={(v) => update("billing_country", v)}
                          labelPrefix="lookups.countries" testId="producer-billing-country-select" />
          </div>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto h-12 px-8 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="producer-save-profile-button">
        {saving ? t("common.loading") : t("producerProfile.saveProfile")}
      </Button>
    </div>
  );
}
